import React, { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../hooks/useAuth';
import service from '../../services/api/supportMirrorService';
import { captureMirrorDocument } from './mirrorDocument';

export default function SupportMirrorCapture() {
  const { currentUser, isImpersonating } = useAuth();
  const { pathname } = useLocation();
  const { i18n } = useTranslation();
  const [session, setSession] = useState(null);
  const [sharingId, setSharingId] = useState(null);
  const [error, setError] = useState('');
  const tabId = useRef(null);
  const actionVersion = useRef(0);
  if (!tabId.current) tabId.current = window.crypto?.randomUUID?.() || `tab-${Date.now()}-${Math.random().toString(36).slice(2)}`;
  const eligible = !isImpersonating && ['employee', 'customer'].includes(currentUser?.userType)
    && pathname.startsWith(`/${currentUser.userType}/`);
  const da = i18n.language?.startsWith('da');

  useEffect(() => {
    if (!eligible) { setSession(null); setSharingId(null); return undefined; }
    let cancelled = false; let timer;
    const poll = async () => {
      const startedVersion = actionVersion.current;
      try {
        const data = await service.current();
        if (!cancelled && startedVersion === actionVersion.current) {
          setSession(data.session);
          if (!data.session || data.session.tabId !== tabId.current) setSharingId(null);
        }
      } catch (_) { if (!cancelled && startedVersion === actionVersion.current) { setSession(null); setSharingId(null); } }
      if (!cancelled) timer = setTimeout(poll, 5000);
    };
    poll();
    return () => { cancelled = true; clearTimeout(timer); };
  }, [eligible, currentUser?.id]);

  useEffect(() => {
    if (!eligible || !sharingId) return undefined;
    let cancelled = false; let timer; let dirty = true; let lastSent = 0; let pointer = null; let clicks = [];
    const mark = () => { dirty = true; };
    const move = (event) => { pointer = { x: event.clientX, y: event.clientY }; mark(); };
    const click = (event) => { move(event); clicks = [...clicks.slice(-9), { ...pointer, button: event.button, at: Date.now() }]; };
    const observer = new MutationObserver(mark);
    observer.observe(document.documentElement, { subtree: true, attributes: true, characterData: true, childList: true });
    const listeners = [['pointermove', move], ['pointerdown', click], ['input', mark], ['change', mark], ['scroll', mark], ['focusin', mark], ['focusout', mark], ['load', mark], ['transitionend', mark], ['animationend', mark]];
    listeners.forEach(([event, listener]) => document.addEventListener(event, listener, true));
    window.addEventListener('resize', mark);
    const send = async () => {
      try {
        if (dirty || Date.now() - lastSent >= 5000) {
          const frame = dirty ? captureMirrorDocument(document, pointer, clicks.filter((item) => Date.now() - item.at < 2500)) : undefined;
          dirty = false;
          await service.publish(sharingId, tabId.current, frame);
          lastSent = Date.now();
        }
      } catch (_) {
        service.stop(sharingId).catch(() => {});
        if (!cancelled) {
          setSharingId(null);
          setError(da ? 'Delingen er stoppet. Start en ny Mirror-session, hvis du stadig har brug for hjælp.' : 'Sharing stopped. Request a new Mirror session if you still need support.');
        }
        return;
      }
      if (!cancelled) timer = setTimeout(send, 500);
    };
    send();
    return () => {
      cancelled = true; clearTimeout(timer); observer.disconnect();
      listeners.forEach(([event, listener]) => document.removeEventListener(event, listener, true));
      window.removeEventListener('resize', mark);
    };
  }, [eligible, sharingId, da]);

  const stop = async () => {
    actionVersion.current++;
    const id = session?.id || sharingId;
    setSharingId(null); setSession(null); setError('');
    if (id) { try { await service.stop(id); } catch (_) { /* Capturing stops immediately; server heartbeat expires. */ } }
    actionVersion.current++;
    setSession(null);
  };
  const approve = async () => {
    actionVersion.current++;
    try {
      await service.approve(session.id, tabId.current);
      actionVersion.current++;
      setSharingId(session.id); setError('');
      setSession((previous) => ({ ...previous, status: 'active', tabId: tabId.current }));
    } catch (err) { setError(err.response?.data?.error?.message || err.message); }
  };
  if (!eligible || (!session && !error)) return null;
  return <aside role="status" style={{ position: 'sticky', top: 0, zIndex: 10000, padding: 12, background: '#fff1c2', borderBottom: '2px solid #9a6700', color: '#422006', textAlign: 'center' }}>
    {error || (session?.status === 'active'
      ? (sharingId === session.id
        ? (da ? 'Support ser din Tempus-side. Du kan stoppe delingen når som helst.' : 'Support is viewing your Tempus page. You can stop sharing at any time.')
        : (da ? 'En support-session er åben i en anden fane. Denne fane deles ikke. Stop sessionen for at starte igen her.' : 'A support session is open in another tab. This tab is not shared. Stop the session to start again here.'))
      : (da ? `${session?.supportName} fra support vil gerne se din Tempus-side, indtastninger og museklik. Adgangskoder skjules.` : `${session?.supportName} from support would like to view your Tempus page, entries and mouse clicks. Passwords are hidden.`))}
    {session?.status === 'pending' && <button type="button" onClick={approve} style={{ marginLeft: 12 }}>{da ? 'Del Tempus-side' : 'Share Tempus page'}</button>}
    <button type="button" onClick={stop} style={{ marginLeft: 12 }}>{session?.status === 'active' ? (da ? 'Stop deling' : 'Stop sharing') : (da ? 'Afvis' : 'Decline')}</button>
  </aside>;
}
